import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  ArrowUpRight,
  MessageSquare,
  Search,
  Home as HomeIcon,
  PhoneCall,
  Palette,
  Globe,
  MapPin,
  MessageCircle,
  Star,
  Workflow,
  LayoutTemplate,
  Sparkles,
  Quote,
} from "lucide-react";
import HeroMockup from "@/components/HeroMockup";
import Marquee from "@/components/Marquee";
import PricingCard from "@/components/PricingCard";
import { Reveal, MaskedLines, fadeUp, stagger } from "@/components/Motion";
import { agentPackages } from "@/data/packages";
import { testimonials } from "@/data/testimonials";
import FAQAccordion from "@/components/FAQAccordion";
import { faqs } from "@/data/faq";

const discoverySteps = [
  {
    n: "01",
    icon: MessageCircle,
    title: "Someone hears your name",
    body: "From a past client, a referral partner, a colleague, a sign, a listing, social media, or an ad.",
  },
  {
    n: "02",
    icon: Search,
    title: "They look you up",
    body: "Google, your website, Instagram, LinkedIn. It takes them under a minute.",
  },
  {
    n: "03",
    icon: Sparkles,
    title: "What they find decides the next call",
    body: "Either your presence backs the recommendation, or it quietly undermines it.",
  },
];

const buildBlocks = [
  {
    n: "01",
    icon: Palette,
    title: "Personal brand identity",
    what: "A logo, colour system, typography, and business card built around your name.",
    why: "Recognisable presence across every touchpoint clients see.",
    handles: "Design, delivery, print files, brand guide.",
  },
  {
    n: "02",
    icon: Globe,
    title: "Personal website",
    what: "A fast, mobile-first site with your listings, story, and lead capture.",
    why: "The one place every profile, ad, and referral link points to.",
    handles: "Design, build, copywriting, hosting, ongoing updates.",
  },
  {
    n: "03",
    icon: MapPin,
    title: "Google & local SEO",
    what: "Google Business Profile plus the site structure that makes you findable locally.",
    why: "Most name-search and near-me traffic starts on Google.",
    handles: "Setup, categories, photos, posts, review link, on-page SEO.",
  },
  {
    n: "04",
    icon: LayoutTemplate,
    title: "Listings & social templates",
    what: "Branded template sets for Just Listed, Just Sold, Open House, Featured, and social posts.",
    why: "Every listing looks like it came from you, not from a random AI generator.",
    handles: "Canva/Adobe templates, sizing for social, web, print, and ads.",
  },
  {
    n: "05",
    icon: MessageSquare,
    title: "Reviews & testimonials",
    what: "Simple systems that ask happy clients for a review at the right moment.",
    why: "Social proof is what turns a warm intro into a booked call.",
    handles: "Review link, request templates, on-site testimonial system.",
  },
  {
    n: "06",
    icon: Workflow,
    title: "Lead capture & follow-up",
    what: "Forms, CRM, missed-call text-back, and email/SMS sequences.",
    why: "Stops leads from silently slipping through when you are busy.",
    handles: "CRM setup, form wiring, automations, review requests.",
  },
];

export default function Home() {
  return (
    <div className="bg-[#0a0a0a] text-white overflow-clip">
      {/* ===================== HERO ===================== */}
      <section
        data-testid="hero-section"
        className="relative pt-6 md:pt-12 pb-16 md:pb-24"
      >
        <div className="absolute inset-0 radial-fade" />
        <div className="absolute inset-0 grain-strong opacity-40" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-[1.1fr_1fr] gap-12 lg:gap-20 items-center">
          <div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.15, duration: 0.6 }}
              className="eyebrow mb-8"
            >
              <span className="gold-line" />
              For real estate agents
            </motion.div>

            <MaskedLines
              testId="hero-headline"
              lines={[
                "The marketing system",
                <>behind <span className="text-[#C8A24B]">agents</span></>,
                "clients already know.",
              ]}
              className="font-heading text-[40px] sm:text-5xl lg:text-[68px] leading-[1.03] tracking-tight text-white"
              delay={0.3}
            />

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.15, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              data-testid="hero-subheadline"
              className="mt-8 text-lg text-[#a1a1aa] leading-relaxed max-w-xl"
            >
              Brand, website, search visibility, content, lead capture, and
              follow-up for real estate agents who win business through
              reputation and referral.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.4, duration: 0.7 }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <Link
                to="/growth-plan"
                data-testid="hero-primary-cta"
                className="btn-gold"
              >
                Build My Growth Plan <ArrowRight size={16} />
              </Link>
              <a
                href="#what-we-build"
                data-testid="hero-secondary-cta"
                className="btn-outline"
              >
                See What We Build
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.6, duration: 0.6 }}
              className="mt-12 pt-8 border-t border-[#27272a] flex items-center gap-6"
            >
              <div className="flex -space-x-2">
                {testimonials.map((t, i) => (
                  <div
                    key={i}
                    className="w-8 h-8 rounded-full border border-[#C8A24B] bg-[#0e0e0e] flex items-center justify-center text-[10px] font-heading text-[#C8A24B]"
                  >
                    {t.name.split(" ").map((n) => n[0]).join("")}
                  </div>
                ))}
              </div>
              <div className="text-[13px] text-[#a1a1aa]">
                Trusted by working agents and mortgage professionals in Ontario.
              </div>
            </motion.div>
          </div>

          <div className="relative">
            <HeroMockup />
          </div>
        </div>
      </section>

      {/* ===================== MARQUEE MANIFESTO ===================== */}
      <Marquee />

      {/* ===================== DISCOVERY JOURNEY (Chapter 01) ===================== */}
      <section
        id="discovery"
        data-testid="discovery-section"
        className="relative py-24 md:py-32 border-b border-[#27272a]"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-[auto_1fr] gap-10 lg:gap-20 items-start mb-16">
            <Reveal>
              <div className="text-[10px] tracking-[0.32em] uppercase text-[#C8A24B]">
                Chapter 01
              </div>
              <div className="font-serif italic text-6xl md:text-7xl lg:text-8xl text-[#C8A24B]/40 leading-none mt-3">
                01
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-heading text-3xl md:text-5xl text-white leading-[1.05] max-w-3xl">
                How clients actually find you.
              </h2>
              <p className="mt-6 text-lg text-[#a1a1aa] max-w-2xl leading-relaxed">
                Nobody wakes up and searches "real estate agent near me" for the
                first time. Real business starts as a name, then becomes a search.
              </p>
            </Reveal>
          </div>

          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
            variants={stagger}
            className="grid md:grid-cols-3 gap-6"
          >
            {discoverySteps.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div
                  key={s.n}
                  custom={i}
                  variants={fadeUp}
                  className="border border-[#27272a] bg-[#0e0e0e] p-8 rounded-sm relative overflow-hidden"
                >
                  <div className="absolute top-6 right-6 font-serif italic text-6xl text-[#C8A24B]/20">
                    {s.n}
                  </div>
                  <div className="w-11 h-11 border border-[#27272a] flex items-center justify-center mb-6">
                    <Icon size={18} className="text-[#C8A24B]" strokeWidth={1.4} />
                  </div>
                  <h3 className="font-heading text-xl text-white mb-3">
                    {s.title}
                  </h3>
                  <p className="text-[#a1a1aa] leading-relaxed">{s.body}</p>
                </motion.div>
              );
            })}
          </motion.div>

          <Reveal delay={0.15}>
            <div className="mt-16 max-w-3xl">
              <p className="font-serif italic text-2xl md:text-3xl text-white leading-snug">
                We make sure what they find backs the recommendation and gives
                them an obvious way to act.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ===================== WHAT WE BUILD (Chapter 02) ===================== */}
      <section
        id="what-we-build"
        data-testid="services-section"
        className="relative py-24 md:py-32 border-b border-[#27272a] bg-[#0d0d0d]"
      >
        <div className="absolute inset-0 grain opacity-30" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-[auto_1fr] gap-10 lg:gap-20 items-start mb-16">
            <Reveal>
              <div className="text-[10px] tracking-[0.32em] uppercase text-[#C8A24B]">
                Chapter 02
              </div>
              <div className="font-serif italic text-6xl md:text-7xl lg:text-8xl text-[#C8A24B]/40 leading-none mt-3">
                02
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-heading text-3xl md:text-5xl text-white leading-[1.05] max-w-3xl">
                What we actually build.
              </h2>
              <p className="mt-6 text-lg text-[#a1a1aa] max-w-2xl leading-relaxed">
                Not advice. Not slides. Real deliverables that live on your
                domain, in your inbox, and in your clients' hands.
              </p>
            </Reveal>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {buildBlocks.map((b, i) => {
              const Icon = b.icon;
              return (
                <Reveal key={b.n} delay={i * 0.05}>
                  <div
                    data-testid={`build-block-${b.n}`}
                    className="border border-[#27272a] hover:border-[#C8A24B]/40 bg-[#0a0a0a] p-8 rounded-sm h-full transition-colors"
                  >
                    <div className="flex items-start justify-between mb-6">
                      <div className="w-11 h-11 border border-[#27272a] flex items-center justify-center">
                        <Icon size={18} className="text-[#C8A24B]" strokeWidth={1.4} />
                      </div>
                      <div className="font-serif italic text-3xl text-[#C8A24B]/30">
                        {b.n}
                      </div>
                    </div>
                    <h3 className="font-heading text-2xl text-white mb-4">
                      {b.title}
                    </h3>
                    <div className="space-y-3 text-sm">
                      <RowKV label="What" value={b.what} />
                      <RowKV label="Why" value={b.why} />
                      <RowKV label="We handle" value={b.handles} accent />
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===================== TESTIMONIALS ===================== */}
      <section
        data-testid="testimonials-section"
        className="relative py-24 md:py-32 border-b border-[#27272a]"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal>
            <div className="text-[10px] tracking-[0.32em] uppercase text-[#C8A24B] mb-6">
              What clients say
            </div>
            <h2 className="font-heading text-3xl md:text-5xl text-white leading-[1.05] max-w-3xl mb-16">
              Grounded results, not hype.
            </h2>
          </Reveal>

          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {testimonials.map((t, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div
                  data-testid={`testimonial-${i}`}
                  className="border border-[#27272a] bg-[#0e0e0e] p-8 md:p-10 rounded-sm relative"
                >
                  <div className="absolute top-0 left-0 right-0 h-[1px] bg-[#C8A24B]" />
                  <Quote size={28} className="text-[#C8A24B]/70 mb-6" strokeWidth={1.3} />
                  <p className="font-serif text-2xl md:text-3xl text-white leading-[1.3] italic mb-8">
                    "{t.quote}"
                  </p>
                  <div className="flex items-center gap-4 pt-6 border-t border-[#27272a]">
                    <div className="w-11 h-11 rounded-full border border-[#C8A24B] bg-[#0a0a0a] flex items-center justify-center text-sm font-heading text-[#C8A24B]">
                      {t.name.split(" ").map((n) => n[0]).join("")}
                    </div>
                    <div>
                      <div className="text-white font-medium">{t.name}</div>
                      <div className="text-xs text-[#a1a1aa]">
                        {t.company} · {t.role}
                      </div>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== COMMISSION MODEL (Chapter 03) ===================== */}
      <section
        data-testid="commission-section"
        className="relative py-24 md:py-32 border-b border-[#27272a] bg-[#0d0d0d]"
      >
        <div className="absolute inset-0 grain opacity-30" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-[auto_1fr] gap-10 lg:gap-20 items-start">
            <Reveal>
              <div className="text-[10px] tracking-[0.32em] uppercase text-[#C8A24B]">
                Chapter 03
              </div>
              <div className="font-serif italic text-6xl md:text-7xl lg:text-8xl text-[#C8A24B]/40 leading-none mt-3">
                03
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-heading text-3xl md:text-5xl text-white leading-[1.05] max-w-3xl">
                Use one closed deal to fund the next.
              </h2>
              <p className="mt-6 text-lg text-[#a1a1aa] max-w-2xl leading-relaxed">
                Agent Marketing packages are flat-fee, not revenue-share. This
                is a mindset, not a billing model: use a small slice of the
                commission from today's transaction to build the marketing
                system that generates tomorrow's opportunity.
              </p>
              <div className="mt-10 grid sm:grid-cols-3 gap-4 max-w-3xl">
                {[
                  { k: "Flat pricing", v: "No % of your commission. Ever." },
                  { k: "Setup + monthly", v: "Predictable cost you can budget." },
                  { k: "Cancel any time", v: "Your accounts and files are yours." },
                ].map((c) => (
                  <div
                    key={c.k}
                    className="border border-[#27272a] bg-[#0a0a0a] p-5 rounded-sm"
                  >
                    <div className="text-[10px] tracking-[0.28em] uppercase text-[#C8A24B] mb-2">
                      {c.k}
                    </div>
                    <div className="text-sm text-[#d4d4d8] leading-relaxed">
                      {c.v}
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ===================== PACKAGES ===================== */}
      <section
        id="packages"
        data-testid="packages-section"
        className="relative py-24 md:py-32 border-b border-[#27272a]"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal>
            <div className="text-[10px] tracking-[0.32em] uppercase text-[#C8A24B] mb-6">
              Packages
            </div>
            <h2 className="font-heading text-3xl md:text-5xl text-white leading-[1.05] max-w-3xl">
              Foundation, Growth, or fully Managed.
            </h2>
            <p className="mt-6 text-lg text-[#a1a1aa] max-w-2xl">
              Three tiers built around how much of the marketing operation you
              want Agent Marketing to run.
            </p>
          </Reveal>

          <div className="mt-16 grid lg:grid-cols-3 gap-6 lg:gap-8">
            {agentPackages.map((pkg, i) => (
              <Reveal key={pkg.id} delay={i * 0.08}>
                <PricingCard pkg={pkg} index={i} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== GROWTH PLAN CTA ===================== */}
      <section className="relative py-24 md:py-32 border-b border-[#27272a] bg-[#0d0d0d] overflow-hidden">
        <div className="absolute inset-0 radial-fade" />
        <div className="relative max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <Reveal>
            <div className="text-[10px] tracking-[0.32em] uppercase text-[#C8A24B] mb-6">
              Interactive
            </div>
            <h2 className="font-heading text-3xl md:text-5xl lg:text-6xl text-white leading-[1.05]">
              Not sure what you need?
              <br />
              <span className="font-serif italic text-[#C8A24B]">
                Take the diagnostic.
              </span>
            </h2>
            <p className="mt-8 text-lg text-[#a1a1aa] max-w-xl mx-auto">
              Six short questions. A real read on your presence, the gaps
              costing you the most, and the exact next step.
            </p>
            <div className="mt-10 flex justify-center gap-4 flex-wrap">
              <Link
                to="/growth-plan"
                data-testid="cta-diagnostic"
                className="btn-gold"
              >
                Build My Growth Plan <ArrowRight size={16} />
              </Link>
              <Link to="/contact" className="btn-outline">
                Skip, just talk
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ===================== FAQ ===================== */}
      <section
        data-testid="faq-section"
        className="relative py-24 md:py-32 border-b border-[#27272a]"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal>
            <div className="text-[10px] tracking-[0.32em] uppercase text-[#C8A24B] mb-6">
              FAQ
            </div>
            <h2 className="font-heading text-3xl md:text-5xl text-white leading-[1.05] max-w-3xl mb-14">
              Answers, plainly said.
            </h2>
          </Reveal>
          <div className="max-w-4xl">
            <FAQAccordion items={faqs.slice(0, 8)} testIdPrefix="home-faq" />
            <div className="mt-8">
              <Link
                to="/faq"
                className="inline-flex items-center gap-2 text-[#C8A24B] font-heading text-sm tracking-wider hover:gap-3 transition-all"
              >
                See all questions
                <ArrowUpRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function RowKV({ label, value, accent = false }) {
  return (
    <div className="grid grid-cols-[80px_1fr] gap-3">
      <div
        className={`text-[10px] tracking-[0.24em] uppercase pt-1 ${
          accent ? "text-[#C8A24B]" : "text-[#71717a]"
        }`}
      >
        {label}
      </div>
      <div className={accent ? "text-white" : "text-[#d4d4d8]"}>{value}</div>
    </div>
  );
}
